import React from 'react'
import { makeStyles, Checkbox, styled, Tooltip } from '@material-ui/core'

import {
  buildShortText,
  buildTooltipText
} from '../../utils/childrenClassrooms'
import Button from '../button/Button'
import UserAvatar from '../shared/UserAvatar'
import { relationOptions } from '../../utils/constants'
import InputSelect from '../select/InputSelect'
import CustomTable from '../table/CustomTable'
import characterMagnifyingGlass from '../../assets/icons/ilustration-character-with-magnifying-glass.svg'

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: 20,
    fontFamily: 'Montserrat, Source Sans Pro',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    height: '100%',
    width: '100%'
  },
  inputs_wrapper: {
    display: 'flex',
    columnGap: 32,
    alignItems: 'flex-start',
    marginBottom: 32
  },
  input_wrapper: {
    width: 325
  },
  avatar_wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    '& p': {
      fontWeight: 500,
      fontSize: 16,
      color: '#555555'
    }
  },
  without_authorization_fallback: {
    marginTop: 52,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '& .fallback_description': {
      '& h1': {
        fontWeight: 600,
        fontSize: 20,
        margin: 0,
        marginBottom: 8
      },
      '& p': {
        color: '#7B7B7B',
        fontWeight: 500,
        fontSize: 12,
        margin: 0
      }
    }
  },
  img_wrapper: {
    width: 240,
    height: 270
  },
  selected_children_preview: {
    marginBottom: 8,
    padding: '12px 32px',
    height: 64,
    backgroundColor: '#F5F6F7',
    borderRadius: 4,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  btns_wrapper: {
    display: 'flex',
    alignItems: 'center',
    columnGap: 16
  },
  root_btn: {
    borderRadius: 8,
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '100%',
    height: 40,
    width: 200,
    textTransform: 'none'
  },
  authorize_pickup_btn: {
    backgroundColor: '#92CB5A',
    '&:hover': {
      backgroundColor: '#92CB5A'
    },
    color: '#FFFFFF'
  },
  clear_selection_btn: {
    backgroundColor: '#868E96',
    '&:hover': {
      backgroundColor: '#868E96'
    },
    color: '#FFFFFF'
  }
}))

const PickupTypeWrappe = styled('div')(({ theme, bgColor }) => ({
  borderRadius: 8,
  padding: 8,
  backgroundColor: bgColor,
  width: 'fit-content',
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& p': {
    fontSize: 16,
    fontWeight: 500
  }
}))

const PickupByResponsibleView = props => {
  const {
    setPickupConfirmationModal,
    getPickupAuthorizations,
    authorizationChildrens,
    getPickupAuthorizationRSPA,
    getPAChild,
    setParentComponentChild
  } = props
  const classes = useStyles()
  const authorizationType = {
    permanent: 'Autorização fixa',
    temporary: 'Somente hoje'
  }
  const [selectedResponsible, setSelectedResponsible] = React.useState([])
  const [selectedChildrens, setSelectedChildrens] = React.useState([])
  const [searchParams, setSearchParams] = React.useState({})

  const columns = [
    {
      key: 'name',
      name: 'Aluno(a)',
      width: '30%',
      render: row => {
        const checked = selectedChildrens.find(
          child => +child.id === +row.child_id
        )
        return (
          <div className={classes.avatar_wrapper}>
            <Checkbox
              className={classes.check}
              checked={checked}
              onChange={() => handleCheck(row.child)}
              color='primary'
            />
            <UserAvatar
              avatarStyle={{
                width: 40,
                height: 40
              }}
              withoutUserName
              userName={row?.child?.name}
              avatarUrl={row?.child?.avatar_url}
            />
            <p>{row?.child?.name}</p>
          </div>
        )
      }
    },
    {
      key: 'classrooms',
      name: 'Turma(s)',
      width: '30%',
      render: row => {
        return (
          <Tooltip title={buildTooltipText([row] || [], true)}>
            <p>{buildShortText([row], 48, true)}</p>
          </Tooltip>
        )
      }
    },
    {
      key: 'authorization_type',
      name: 'Tipo',
      width: '30%',
      render: row => {
        return (
          <PickupTypeWrappe
            bgColor={
              row?.pickup_authorization?.pickup_type === 'permanent'
                ? '#D9E8FA'
                : '#F8E7D6'
            }
          >
            <p>{authorizationType[row?.pickup_authorization?.pickup_type]}</p>
          </PickupTypeWrappe>
        )
      }
    }
  ]
  const handleCheck = obj => {
    setSelectedChildrens(prev => {
      const exists = prev.some(item => +item.id === +obj.id)
      const newData = exists
        ? prev.filter(item => +item.id !== +obj.id)
        : [...prev, obj]
      setParentComponentChild(newData)
      return newData
    })
  }

  const handleClearSelectedChildrens = () => {
    setSelectedChildrens([])
    setParentComponentChild([])
  }

  React.useEffect(
    () => {
      if (selectedResponsible.length > 0) {
        const params = {
          'q[pickup_authorization_id_eq]': selectedResponsible?.[0]?.id,
          include: 'child.classrooms,pickup_authorization'
        }
        setSearchParams(params)
        getPAChild({
          params
        })
      }
    },
    [selectedResponsible]
  )

  return (
    <div className={classes.root}>
      <div className={classes.inputs_wrapper}>
        <InputSelect
          wrapperClass={classes.input_wrapper}
          fetchItems={getPickupAuthorizationRSPA}
          selectedItems={selectedResponsible}
          setSelectedItems={setSelectedResponsible}
          attributesFromItem={[
            'photo_url',
            'name',
            'relationship_type',
            'pickup_type',
            'vehicle_plate'
          ]}
          placeholder={'Digite o nome do responsável'}
          label={'Responsável'}
          params={{
            'page[size]': 7
          }}
        />
      </div>
      {selectedChildrens.length > 0 && (
        <div className={classes.selected_children_preview}>
          <p>{selectedChildrens.length} alunos selecionados</p>
          <div className={classes.btns_wrapper}>
            <Button
              onclick={() => {
                setPickupConfirmationModal({
                  id: selectedResponsible?.[0].id,
                  open: true,
                  data: {
                    responsible_name: selectedResponsible?.[0]?.name,
                    responsible_photo: selectedResponsible?.[0]?.photo_url,
                    responsible_type: relationOptions.find(
                      relationship =>
                        relationship.value ===
                        selectedResponsible?.[0]?.relationship_type
                    )?.label,
                    authorization_data: {
                      type:
                        authorizationType[
                          selectedResponsible?.[0]?.pickup_type
                        ],
                      vehicle_plate: selectedResponsible?.[0]?.vehicle_plate?.toUpperCase()
                    }
                  }
                })
              }}
              type='button'
              className={`${classes.root_btn} ${classes.authorize_pickup_btn}`}
            >
              {`Autorizar saída (${selectedChildrens.length})`}
            </Button>
            <Button
              onclick={() => handleClearSelectedChildrens()}
              type='button'
              className={`${classes.root_btn} ${classes.clear_selection_btn}`}
            >
              Limpar seleção
            </Button>
          </div>
        </div>
      )}
      {selectedResponsible?.[0] ? (
        <CustomTable
          columns={columns}
          from={'authorizations'}
          noDataAlert={
            'Usuário selecionado não possui criança autorizada para a retirada.'
          }
          data={authorizationChildrens.items}
          fetchItems={getPickupAuthorizations}
          params={{
            ...searchParams,
            include:
              'user.child_parents,authorized_by,pickup_authorization_children.child.classrooms,school'
          }}
          isFetching={authorizationChildrens.isFetching}
          pagination={authorizationChildrens.pagination}
          columnsPointer={[0]}
          border={'none'}
          headCellRadius
          headColor={'rgba(173, 184, 204, 0.3)'}
        />
      ) : (
        <div className={classes.without_authorization_fallback}>
          <img className={classes.img_wrapper} src={characterMagnifyingGlass} />
          <div className='fallback_description'>
            <h1>Configure os filtros para começar</h1>
            <p>Selecione o aluno ou o responsável para iniciar a busca</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default PickupByResponsibleView
